import { Box, Typography } from '@mui/material'
import Tooltip from './ReportTooltip'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faQuestionCircle } from '@fortawesome/free-solid-svg-icons'
import { ListMagnifyingGlass, MapTrifold } from '@phosphor-icons/react'
import { Table } from 'react-bootstrap'
import AppChip from '../../../components/chips/AppChip'
import UbiquaTablePagination from '../../../components/table/UbiquaTablePagination'
import dayjs from '../../../utils/dayjsConfig'
import { effectiveLocationStr, formatTimetoHHmm } from '../../../helpers/utils'
import { getGeofenceStatus } from '../helpers/geofenceStatus'

const TABLE_HEADERS = [
  { label: 'Fecha', description: 'Fecha de la visita.' },
  { label: 'Cliente', description: 'Código y nombre del cliente visitado.' },
  { label: 'Hora de inicio', description: 'Hora en que inició la visita.' },
  { label: 'Hora de finalización', description: 'Hora en que finalizó la visita.' },
  { label: 'Duración de visita', description: 'Tiempo total de la visita.' },
  { label: 'Dentro de geocerca', description: 'Indica si la visita fue dentro de geocerca.' },
  { label: 'Ubicación', description: 'Abre el mapa de la visita.' }
]

const CENTER_HEADERS = new Set(['Dentro de geocerca', 'Ubicación'])

const getGeofenceLabel = (row) =>
  row.effective_location != null
    ? effectiveLocationStr(row.effective_location, row.client_latitude, row.start_latitude)
    : 'N/A'

const getClientCode = (row) => row.client_db_ref || row.client_uid

export default function TimestampControlTable({
  rows = [],
  currentPage,
  totalPages,
  totalCount,
  perPage,
  onPageChange,
  onRowsPerPageChange,
  onOpenLocation
}) {
  const paginationTotalCount = Math.max(0, Number(totalCount) || rows.length)
  const shouldShowPagination = paginationTotalCount > 10

  const emptyState = (
    <Box
      sx={{
        minHeight: 220,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        px: 3,
        py: 4
      }}
    >
      <Box sx={{ maxWidth: 360 }}>
        <Box
          sx={{
            width: 66,
            height: 66,
            borderRadius: '50%',
            bgcolor: '#F2F6FB',
            color: '#8FA4BC',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 1.75
          }}
        >
          <ListMagnifyingGlass size={34} weight="regular" />
        </Box>
        <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#1A2733', mb: 1 }}>
          No hay visitas registradas
        </Typography>
        <Typography sx={{ fontSize: 8, lineHeight: 1.55, color: '#6B7A8C' }}>
          No se registraron visitas para este usuario durante el período seleccionado.
        </Typography>
      </Box>
    </Box>
  )

  return (
    <div className="route-tower-table-shell">
      <div className="route-tower-table-card">
        <div className="route-tower-scroll">
          <Table hover responsive className="route-tower-table">
            <thead className="table-header-gray-mui">
              <tr>
                {TABLE_HEADERS.map((header) => {
                  const centered = CENTER_HEADERS.has(header.label)
                  return (
                    <th key={header.label} className={centered ? 'align-center-table-column' : ''}>
                      <Tooltip title={header.description} placement="top" arrow>
                        <div
                          className={`table-details-header-container ${
                            centered ? 'justify-content-center' : ''
                          }`}
                        >
                          <span className="th-header-text">{header.label}</span>
                          <FontAwesomeIcon
                            icon={faQuestionCircle}
                            className="table-header-help-icon"
                          />
                        </div>
                      </Tooltip>
                    </th>
                  )
                })}
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={TABLE_HEADERS.length} style={{ padding: 0 }}>
                    {emptyState}
                  </td>
                </tr>
              ) : (
                rows.map((row, index) => {
                  const completed = row.status === 'completed'
                  return (
                    <tr
                      key={`${row.client_name}-${row.visited_date}-${row.start_visit_time}-${index}`}
                    >
                      <td className="td-text-limited route-cell-tabular">
                        {dayjs(row.visited_date).format('DD/MM/YYYY')}
                      </td>
                      <td className="td-text-limited">
                        <Tooltip
                          title={
                            [getClientCode(row), row.client_name].filter(Boolean).join(' - ') || '-'
                          }
                          placement="top"
                          arrow
                        >
                          <span className="route-client-cell">
                            <span className="route-client-name">{row.client_name || '-'}</span>
                            {getClientCode(row) ? (
                              <span className="route-client-code">{getClientCode(row)}</span>
                            ) : null}
                          </span>
                        </Tooltip>
                      </td>
                      <td className="td-text-limited route-cell-tabular">
                        {formatTimetoHHmm(row.start_visit_time)}
                      </td>
                      <td className="td-text-limited route-cell-tabular">
                        {completed ? formatTimetoHHmm(row.end_visit_time) : 'En curso'}
                      </td>
                      <td className="td-text-limited route-cell-tabular">
                        {completed ? row.visit_duration || '-' : '-'}
                      </td>
                      <td className="td-text-limited align-center-table-column">
                        <AppChip {...getGeofenceStatus(getGeofenceLabel(row))} />
                      </td>
                      <td className="td-text-limited align-center-table-column">
                        <Tooltip title="Ver ubicación" placement="top" arrow>
                          <span>
                            <button
                              type="button"
                              className="route-location-action"
                              aria-label="Ver ubicación"
                              onClick={(event) => onOpenLocation(row, event)}
                            >
                              <MapTrifold size={24} weight="regular" aria-hidden="true" />
                            </button>
                          </span>
                        </Tooltip>
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </Table>
        </div>
      </div>
      {shouldShowPagination ? (
        <UbiquaTablePagination
          ariaLabel="Paginación de control de horario"
          itemLabel="visitas"
          emptyLabel="Sin visitas para mostrar"
          showPageSizeControl
          pagination={{
            total_count: paginationTotalCount,
            per_page: perPage,
            total_pages: Math.max(1, totalPages || 1),
            current_page: currentPage
          }}
          onPageChange={(_, page) => onPageChange?.(page)}
          onRowsPerPageChange={onRowsPerPageChange}
        />
      ) : null}
    </div>
  )
}
